import { DateRangePickerProps, Header, SpaceBetween } from '@amzn/awsui-components-react-v3';
import { OptionDefinition } from '@amzn/awsui-components-react-v3/polaris/internal/components/option/interfaces';
import _ from 'lodash';
import moment from 'moment';
import React, { ReactElement } from 'react';
import { Link, useHistory } from 'react-router-dom';
import KeyValue from '../../../components/KeyValue';
import { ExtendedMessageDefinition } from '../../../components/Notification/types';
import { PromptTypes } from '../../../components/Prompt';
import { PromptOptions } from '../../../components/Prompt/PromptProvider/types';
import i18n from '../../../i18n';
import {
  ReceiveException,
  ReceiveExceptionStatus,
} from '../../../services/receive-exception/types';
import { humanizeDate } from '../../../utils';
import {
  ExceptionStatusOptions,
  ExceptionTypes,
  OrderTypeOptions,
  ResolutionCodeOptions,
  ExceptionTypeOptionsProps,
} from './config';
import { ExceptionFormValues } from './ExceptionForm/schema';
import {
  ExceptionSearchFilters,
  ReceiveExceptionSearchResult,
  ReceiveExceptionView,
  SearchFilterValues,
} from './types';

export function getCreateSuccessNotification(
  exceptionId: string,
  history: ReturnType<typeof useHistory>,
  clearNotification: (groupName: string) => void
): ExtendedMessageDefinition {
  return {
    header: i18n.t('receive-exception.create-success-message', { exceptionId }),
    buttonText: i18n.t('View Exception'),
    buttonClick: (): void => {
      history.push(`/receive/exception/${exceptionId}`);
      clearNotification('receive-exception-create');
    },
    dismissible: true,
    type: 'success',
    group: 'receive-exception-create',
  };
}
export function transformReceiveExceptionError(error: Error): string {
  const errorMessageFromServer = _.get(error, 'graphQLErrors[0].extensions.exception.message');
  const errorCodeFromServer = _.get(error, 'graphQLErrors[0].extensions.exception.code');
  if (!/internal.?server.?error/gi.test(errorCodeFromServer) && errorMessageFromServer) {
    return i18n.t(errorMessageFromServer);
  }

  if (error.message === 'INVALID_SITE') {
    return i18n.t('Provide a valid site name');
  }
  if (error.message.includes('status code 400')) {
    return i18n.t(
      'Unexpected error occurred with the request. Please try again, If the error persists create a ticket'
    );
  }
  return `${i18n.t('Unexpected error occurred.')} ${error.message}`;
}

export function getCreateFailureNotification(error: Error): ExtendedMessageDefinition {
  return {
    header: i18n.t('Failed to create receiving exception.'),
    content: transformReceiveExceptionError(error),
    dismissible: true,
    type: 'error',
    group: 'receive-exception-create',
  };
}

export function transformExceptionForForm(
  exception: ReceiveException,
  exceptionTypes: ExceptionTypeOptionsProps[]
): ExceptionFormValues {
  return {
    ...exception,

    entireOrder: !exception.orderLineNumber,
    serial: '',
    tag: '',
    note: '',

    assignedToId: {
      value: exception.assignedToId,
      label: exception.assignedToId,
    } as OptionDefinition,
    exceptionType: _.find(exceptionTypes, {
      value: exception.exceptionType,
    }) as OptionDefinition,
    orderType: _.find(OrderTypeOptions, { value: exception.orderType }) as OptionDefinition,
    serials: exception.serials.map((item: string) => ({ label: item })),
    tags: exception.tags.map((item: string) => ({ label: item })),
  };
}

export function transformExceptionForView(
  exception: ReceiveException,
  exceptionTypes: ExceptionTypeOptionsProps[]
): ReceiveExceptionView {
  return {
    ...exception,
    exceptionType:
      (
        _.find(exceptionTypes, {
          value: exception.exceptionType,
        }) as OptionDefinition
      )?.label || exception.exceptionType,
    orderType:
      (_.find(OrderTypeOptions, { value: exception.orderType }) as OptionDefinition)?.label || '',
    createdAt: humanizeDate(exception.createdAt),
    updatedAt: exception.updatedAt ? humanizeDate(exception.updatedAt) : '',
    resolutionCode:
      (_.find(ResolutionCodeOptions, { value: exception.resolutionCode }) as OptionDefinition)
        ?.label || '',
    isEditable: exception.status === ReceiveExceptionStatus.ASSIGNED,
    isResolvable: exception.status === ReceiveExceptionStatus.PENDING_ACTION,
    isCancellable:
      exception.status !== ReceiveExceptionStatus.RESOLVED &&
      exception.status !== ReceiveExceptionStatus.CANCELLED,
    resolutionCodeOption: _.find(ResolutionCodeOptions, {
      value: exception.resolutionCode,
    }) as OptionDefinition,
  };
}

export function getUpdateSuccessNotification(): ExtendedMessageDefinition {
  return {
    header: i18n.t('Exception details updated successfully'),
    dismissible: true,
    type: 'success',
    group: 'receive-exception-update',
  };
}

export function getUpdateFailureNotification(error: Error): ExtendedMessageDefinition {
  return {
    header: i18n.t('Failed to update receiving exception.'),
    content: error ? transformReceiveExceptionError(error) : '',
    dismissible: true,
    type: 'error',
    group: 'receive-exception-update',
  };
}

export function getResolveConfirmationPrompt(data: ReceiveExceptionView): PromptOptions {
  return {
    type: PromptTypes.CONFIRM,
    title: i18n.t('Close Exception'),
    body: (
      <div>
        <SpaceBetween direction="vertical" size="l">
          <span>{`${i18n.t('Are you sure you want to close the exception')} ${
            data.exceptionId
          }?`}</span>
          <Header variant="h2">{i18n.t('Resolution Details')}</Header>
          <KeyValue label={i18n.t('Resolution Code Provider')}>{data?.resolver}</KeyValue>
          <KeyValue label={i18n.t('Resolution Code')}>{data?.resolutionCode}</KeyValue>
          <KeyValue label={i18n.t('Resolution Notes')}>{data?.resolutionNote}</KeyValue>
        </SpaceBetween>
      </div>
    ),
  };
}
export function getCancelConfirmationPrompt(): PromptOptions {
  return {
    type: PromptTypes.CONFIRM,
    title: i18n.t('Cancel Exception'),
    body: i18n.t(
      'Are you sure you want to cancel the exception. No other action will be able to be taken and you wont be able to undo the cancellation'
    ),
  };
}

export function transformSearchResults(
  searchResults: ReceiveException[],
  exceptionTypes: ExceptionTypeOptionsProps[]
): ReceiveExceptionSearchResult[] {
  return searchResults.map((result: ReceiveException) => {
    let exceptionTypeOption = _.find(exceptionTypes, {
      value: result.exceptionType,
    }) as OptionDefinition;
    if (!exceptionTypeOption) {
      exceptionTypeOption = {
        label: result.exceptionType,
        value: result.exceptionType,
      } as OptionDefinition;
    }

    const statusOption =
      (_.find(ExceptionStatusOptions, {
        value: result.status,
      }) as OptionDefinition) || '';

    return {
      ...result,
      exceptionType: exceptionTypeOption?.label as ExceptionTypes,
      status: statusOption?.label as ReceiveExceptionStatus,
      exceptionTypeOption,
      statusOption,
    };
  });
}

export function renderExceptionLink(e: ReceiveExceptionSearchResult): ReactElement {
  return <Link to={`/receive/exception/${e.id}`}>{`${e.id.substring(0, 12)}...`}</Link>;
}

export function renderField(fieldName: string) {
  return (e: ReceiveExceptionSearchResult): string => {
    return _.get(e, fieldName) || '-';
  };
}

export function renderList(fieldName: string) {
  return (e: ReceiveExceptionSearchResult): string => {
    const foundEntries = _.get(e, fieldName);
    if (!foundEntries) {
      return '-';
    }
    let runningResult = '';
    foundEntries.forEach((value) => {
      runningResult = `${runningResult + value}, `;
    });
    // Trim
    const resultingList: string = runningResult.trim();
    if (!resultingList) {
      // Handle cases where an empty list is in the ReceiveException object
      return '-';
    }
    // Remove comma at end of the list
    return resultingList.slice(0, -1);
  };
}

export function renderDateField(fieldName: string) {
  return (result: ReceiveExceptionSearchResult): string => {
    try {
      return `${humanizeDate(_.get(result, fieldName) as number)}`;
    } catch (e) {
      return '-';
    }
  };
}

export function getDateFromDateRange(value: DateRangePickerProps.Value | null): {
  startDate?: string;
  endDate?: string;
} {
  let startDate;
  let endDate;
  if (value?.type === 'relative') {
    endDate = moment().toISOString();
    startDate = moment()
      .subtract(
        (value as DateRangePickerProps.RelativeValue).amount,
        (value as DateRangePickerProps.RelativeValue).unit
      )
      .toISOString();
  } else if (value?.type === 'absolute') {
    startDate = value?.startDate;
    endDate = value?.endDate;
  }
  return { startDate, endDate };
}

export function transformSearchFilters(filters: SearchFilterValues): ExceptionSearchFilters {
  const createdAt = getDateFromDateRange(filters.createdAt);

  // ToDo: Receive Exception Search GraphQL schema is still referring to `assignedTo` field instead of
  // `assignedToId` field. After fixing it, remove the `assignedToId` field from omit list.
  return {
    ..._.omit(filters, ['createdAt', 'assignedToId']),
    status: filters.status ? filters.status.value : null,
    exceptionType: filters.exceptionType ? filters.exceptionType.value : null,
    createdAtFrom: createdAt?.startDate ? moment(createdAt?.startDate).valueOf() : null,
    createdAtTo: createdAt?.endDate ? moment(createdAt?.endDate).valueOf() : null,
    assignedTo: filters.assignedToId,
  } as ExceptionSearchFilters;
}
